import { useEffect, useState } from 'react';
import { Button, Modal, Spinner } from '../ui';
import { contentIngestionService, type PublicationPreview, type PublicationSelection } from '../../services/contentIngestionService';

export default function PublishDialog({ open, eventId, selection, onClose, onPublished }: {
  open: boolean;
  eventId: string;
  selection: PublicationSelection;
  onClose: () => void;
  onPublished: () => void | Promise<void>;
}) {
  const [preview, setPreview] = useState<PublicationPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setLoading(true); setPreview(null); setError(null);
    void contentIngestionService.previewPublication(eventId, selection)
      .then(setPreview).catch((cause) => setError(cause instanceof Error ? cause.message : 'Unable to preview publication'))
      .finally(() => setLoading(false));
  }, [eventId, open, selection]);

  const requested = selection.areas ?? [];
  const dependencies = preview?.content_areas.filter((area) => !requested.includes(area as never)) ?? [];
  const label = selection.all_ready ? 'Publish all ready changes' : `Publish ${requested.join(' and ')}`;
  async function publish() {
    setPublishing(true); setError(null);
    try { await contentIngestionService.publish(eventId, selection); await onPublished(); onClose(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'Publication failed because the draft changed. Refresh and review the conflicts.'); }
    finally { setPublishing(false); }
  }

  return <Modal open={open} onClose={onClose} title="Confirm publication" size="lg">
    {loading ? <div className="flex justify-center p-8"><Spinner className="h-5 w-5" /></div> : preview ? <div className="space-y-4">
      <p className="text-sm text-gray-300">{preview.change_ids.length} changes</p>
      <div className="grid grid-cols-3 gap-3">{(['created', 'updated', 'deleted'] as const).map((operation) => <div key={operation} className="rounded-lg border border-gray-800 p-3"><p className="text-xs capitalize text-gray-500">{operation}</p><p className="mt-1 text-lg font-semibold text-white">{preview.summary[operation] ?? 0}</p></div>)}</div>
      {dependencies.map((area) => <p key={area} className="text-sm text-amber-300">{area.charAt(0).toUpperCase() + area.slice(1)} included as a dependency</p>)}
      <p className="text-xs text-gray-500">Publishing is atomic. If any selected record is stale or invalid, nothing will be changed.</p>
      {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
      <div className="flex justify-end gap-2"><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={publishing} onClick={() => void publish()}>{label}</Button></div>
    </div> : <div>{error && <p role="alert" className="text-sm text-red-300">{error}</p>}<div className="mt-4 flex justify-end"><Button variant="secondary" onClick={onClose}>Close</Button></div></div>}
  </Modal>;
}
